import React, { useState } from 'react';

const ContactForm = () => {
  // Form state using the Formspree approach
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });
  
  const [inputs, setInputs] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  
  // Track which fields have been touched for validation
  const [touched, setTouched] = useState({});
  
  // Validation state
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate field on change
    validateField(name, value);
  };

  // Handle blur events to set touched state
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, inputs[name]);
  };

  // Validate a single field
  const validateField = (name, value) => {
    let fieldErrors = { ...errors };
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          fieldErrors.name = 'Name is required';
        } else {
          delete fieldErrors.name;
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          fieldErrors.email = 'Email is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          fieldErrors.email = 'Invalid email address';
        } else {
          delete fieldErrors.email;
        }
        break;
        
      case 'phone':
        if (value.trim() && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/i.test(value)) {
          fieldErrors.phone = 'Invalid phone number';
        } else {
          delete fieldErrors.phone;
        }
        break;
        
      case 'message':
        if (!value.trim()) {
          fieldErrors.message = 'Message is required';
        } else if (value.trim().length < 10) {
          fieldErrors.message = 'Message must be at least 10 characters';
        } else {
          delete fieldErrors.message;
        }
        break;
        
      default:
        break;
    }
    
    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  // Validate all fields
  const validateForm = () => {
    // Trigger validation for all fields
    const fieldNames = ['name', 'email', 'phone', 'message'];
    let formIsValid = true;
    
    fieldNames.forEach(field => {
      const isFieldValid = validateField(field, inputs[field]);
      if (!isFieldValid) formIsValid = false;
    });
    
    // Mark all fields as touched
    const touchedFields = {};
    fieldNames.forEach(field => touchedFields[field] = true);
    setTouched(touchedFields);
    
    return formIsValid;
  };

  // Handle form submission using Formspree
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    if (!validateForm()) {
      return;
    }
    
    setStatus(prevStatus => ({ ...prevStatus, submitting: true }));
    
    try {
      // Use Formspree endpoint
      const response = await fetch('https://formspree.io/f/mdkegdbq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inputs)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatus({
          submitted: true,
          submitting: false,
          info: { error: false, msg: "Thank you for contacting Eluno Consulting! We'll review your inquiry and respond within one business day." }
        });
        
        // Reset form after successful submission
        setInputs({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        setTouched({});
      } else {
        setStatus({
          submitted: false,
          submitting: false,
          info: { error: true, msg: data.error || 'Failed to submit form. Please try again later.' }
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: 'There was an error submitting your message. Please try again later.' }
      });
    }
  };

  // Check if the form is valid (for enabling/disabling submit button)
  const isFormValid = () => {
    const requiredFields = ['name', 'email', 'message'];
    return requiredFields.every(field => 
      inputs[field].trim() !== '' && !errors[field]
    );
  };

  // Get class name for form inputs based on validation state
  const getInputClassName = (fieldName) => {
    const baseClasses = 'w-full px-4 py-3 bg-dark-light border rounded-md text-light focus:outline-none focus:ring-2 transition-all duration-200';
    
    if (!touched[fieldName]) return `${baseClasses} border-light/20 focus:ring-accent/50`;
    
    if (errors[fieldName]) {
      return `${baseClasses} border-red-500 focus:ring-red-500/50`;
    }
    
    return `${baseClasses} border-green-500 focus:ring-accent/50`;
  };

  // If form was successfully submitted, show success message
  if (status.submitted) {
    return (
      <div className="bg-dark-light border border-accent/30 rounded-lg p-8 shadow-lg mb-10">
        <div className="flex items-center justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-light text-center mb-4">Message Sent!</h3>
        <p className="text-light/90 text-center mb-6">
          {status.info.msg}
        </p>
        <div className="flex justify-center">
          <button 
            onClick={() => setStatus(prev => ({ ...prev, submitted: false }))} 
            className="px-6 py-3 bg-accent text-dark rounded-md font-medium hover:bg-accent/90 transition-all"
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-dark-light rounded-lg p-6 md:p-8 shadow-lg">
      <div className="mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-light mb-2">Get in Touch</h2>
        <p className="text-light/80">
          Fill out the form below and we'll get back to you within one business day.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-light font-medium mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={inputs.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={getInputClassName('name')}
            placeholder="Your name"
            required
          />
          {touched.name && errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name}</p>
          )}
        </div>
        
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-light font-medium mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={inputs.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={getInputClassName('email')}
            placeholder="your@email.com"
            required
          />
          {touched.email && errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email}</p>
          )}
        </div>
        
        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-light font-medium mb-2">
            Phone (optional)
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={inputs.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={getInputClassName('phone')}
            placeholder="+1 (555) 555-5555"
          />
          {touched.phone && errors.phone && (
            <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
          )}
        </div>
        
        {/* Company Field */}
        <div>
          <label htmlFor="company" className="block text-light font-medium mb-2">
            Company (optional)
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={inputs.company}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-dark-light border border-light/20 rounded-md text-light focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all duration-200`}
            placeholder="Your company"
          />
        </div>
      </div>
      
      {/* Message Field */}
      <div className="mb-8">
        <label htmlFor="message" className="block text-light font-medium mb-2">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={inputs.message}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`${getInputClassName('message')} h-36 resize-none`}
          placeholder="How can we help you?"
          required
        ></textarea>
        {touched.message && errors.message && (
          <p className="mt-1 text-sm text-red-500">{errors.message}</p>
        )}
      </div>
      
      {/* Submit button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!isFormValid() || status.submitting}
          className={`px-6 py-3 rounded-md font-medium transition-all duration-300 ease-in-out relative ${
            isFormValid() && !status.submitting
              ? 'bg-accent text-dark hover:bg-accent/90 shadow-lg shadow-accent/20 hover:shadow-accent/30'
              : 'bg-gray-600 text-light/50 cursor-not-allowed'
          }`}
        >
          {status.submitting ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </div>
          ) : (
            'Send Message'
          )}
        </button>
      </div>
      
      {/* Error message */}
      {status.info.error && (
        <div className="mt-4 p-3 bg-red-500/20 border border-red-500 rounded text-red-500 text-sm">
          {status.info.msg}
        </div>
      )}
    </form>
  );
};

export default ContactForm;